<script lang="ts">
  // Forced first-login password change.
  //
  // Rendered by Class.svelte as a full-screen guard whenever the logged-in
  // user has `must_change_password === true` (admin created the account with an
  // initial password). The student cannot reach the classroom controls until
  // the change succeeds. On success we clear the session flag and the guard
  // releases, letting Class render normally.
  import { changePassword, ApiError, e2eeStatus } from "../lib/api";
  import { setMustChangePassword } from "../lib/session.svelte";
  import { rewrapIdentityForNewPassword } from "../lib/e2eeSession";
  import { t, _ } from "../lib/i18n";
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher<{ changed: void }>();

  let currentPassword = "";
  let newPassword = "";
  let confirm = "";
  let submitting = false;
  let error: string | null = null;
  // Set when the password changed server-side but the E2EE key could NOT be
  // re-wrapped under the new password (it must now be restored via recovery /
  // re-added). Surfaced — never swallowed — so the student is not silently
  // decoupled from their encrypted media.
  let rewrapFailed = false;

  async function submit(e: Event) {
    e.preventDefault();
    error = null;
    if (!currentPassword || !newPassword) {
      error = t("changePw.required");
      return;
    }
    if (newPassword !== confirm) {
      error = t("changePw.mismatch");
      return;
    }
    if (newPassword === currentPassword) {
      error = t("changePw.sameAsCurrent");
      return;
    }
    submitting = true;
    rewrapFailed = false;
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Keep the E2EE identity usable across this forced change: re-wrap the
      // private key under the new password using the login (current) password,
      // which IS available here. Mirrors Profile.submitPassword. The password
      // change has already succeeded server-side, so a re-wrap failure must be
      // surfaced (recovery needed) rather than blocking the guard release.
      let enabled = false;
      try {
        enabled = (await e2eeStatus()).enabled === true;
      } catch {
        enabled = false;
      }
      if (enabled) {
        try {
          await rewrapIdentityForNewPassword(currentPassword, newPassword);
        } catch {
          rewrapFailed = true;
        }
      }
      // Clear the flag locally so the guard releases immediately; the backend
      // has already cleared it server-side. If the re-wrap failed, still release
      // (the password change stands) but the warning below stays visible.
      setMustChangePassword(false);
      dispatch("changed");
    } catch (err) {
      error =
        err instanceof ApiError && (err.status === 401 || err.status === 403)
          ? t("changePw.wrongCurrent")
          : err instanceof ApiError
            ? err.message
            : t("changePw.failed");
    } finally {
      submitting = false;
    }
  }
</script>

<section class="login" data-testid="change-password">
  <div class="card">
    <h1>{$_("changePw.title")}</h1>
    <p class="sub">{$_("changePw.help")}</p>
    <p class="note" data-testid="change-e2ee-note">{$_("changePw.e2eeNote")}</p>
    <form on:submit={submit}>
      <label>
        {$_("changePw.current")}
        <input
          type="password"
          autocomplete="current-password"
          bind:value={currentPassword}
          data-testid="current-password"
          required
        />
      </label>
      <label>
        {$_("changePw.new")}
        <input
          type="password"
          autocomplete="new-password"
          bind:value={newPassword}
          data-testid="new-password"
          required
        />
      </label>
      <label>
        {$_("changePw.confirm")}
        <input
          type="password"
          autocomplete="new-password"
          bind:value={confirm}
          data-testid="confirm-password"
          required
        />
      </label>
      <button type="submit" disabled={submitting} data-testid="change-submit">
        {submitting ? $_("changePw.saving") : $_("changePw.save")}
      </button>
      {#if error}<p class="error" data-testid="change-error">{error}</p>{/if}
      {#if rewrapFailed}
        <p class="error" data-testid="change-rewrap-error">
          {$_("changePw.rewrapFailed")}
        </p>
      {/if}
    </form>
  </div>
</section>

<style>
  .login {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1rem;
    /* iOS viewport-fit=cover: keep the card out from under the status bar and
       home indicator. env() is 0 elsewhere, so 1rem is preserved. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: 100%;
    max-width: 360px;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  }
  h1 {
    margin: 0 0 0.25rem;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
  }
  .note {
    margin: 0 0 1rem;
    padding: 0.5rem 0.65rem;
    background: rgba(252, 211, 77, 0.1);
    border: 1px solid #b45309;
    border-radius: 8px;
    color: #fcd34d;
    font-size: 0.8rem;
  }
  form {
    display: grid;
    gap: 0.75rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.875rem;
    color: #d1d5db;
  }
  input {
    padding: 0.6rem 0.75rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 8px;
    font-size: 1rem;
  }
  input:focus {
    outline: 2px solid #10b981;
    outline-offset: 1px;
  }
  button {
    padding: 0.6rem 0.75rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.875rem;
  }
</style>
